import { useEffect, useMemo, useState } from "react";
import {
  Building2,
  CheckCircle2,
  Cloud,
  Copy,
  Save,
  Settings,
  Store,
  UserRound,
} from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Card, InlineNotice, PageHeader, StatusBadge } from "../components/ui";

interface StoreData {
  id: string;
  name: string;
  businessType?: string | null;
  phone?: string | null;
  email?: string | null;
  website?: string | null;
  address?: string | null;
  timezone: string;
  updatedAt?: string;
}

type LegalEntityType =
  | "HOUSEHOLD_BUSINESS"
  | "INDIVIDUAL_BUSINESS"
  | "SOLE_PROPRIETORSHIP"
  | "SINGLE_MEMBER_LLC"
  | "MULTI_MEMBER_LLC"
  | "JOINT_STOCK_COMPANY"
  | "COOPERATIVE"
  | "OTHER";

interface BusinessProfile {
  legalNameVi?: string | null;
  legalNameEn?: string | null;
  shortName?: string | null;
  legalEntityType?: LegalEntityType | null;
  taxCode?: string | null;
  registeredAddress?: string | null;
  phone?: string | null;
  email?: string | null;
  website?: string | null;
  representativeName?: string | null;
  representativeTitle?: string | null;
  representativePhone?: string | null;
  representativeEmail?: string | null;
}

interface BusinessProfileResponse {
  profile: BusinessProfile | null;
  completeness: {
    completed: number;
    total: number;
    percent: number;
    missing: string[];
  };
  suggestions?: Partial<BusinessProfile>;
}

const legalEntityLabels: Record<LegalEntityType, string> = {
  HOUSEHOLD_BUSINESS: "Hộ kinh doanh",
  INDIVIDUAL_BUSINESS: "Cá nhân kinh doanh",
  SOLE_PROPRIETORSHIP: "Doanh nghiệp tư nhân",
  SINGLE_MEMBER_LLC: "Công ty TNHH một thành viên",
  MULTI_MEMBER_LLC: "Công ty TNHH hai thành viên trở lên",
  JOINT_STOCK_COMPANY: "Công ty cổ phần",
  COOPERATIVE: "Hợp tác xã",
  OTHER: "Tổ chức / loại hình khác",
};

const emptyBusinessForm = {
  legalNameVi: "",
  legalNameEn: "",
  shortName: "",
  legalEntityType: "" as LegalEntityType | "",
  taxCode: "",
  registeredAddress: "",
  phone: "",
  email: "",
  website: "",
  representativeName: "",
  representativeTitle: "",
  representativePhone: "",
  representativeEmail: "",
};

function optional(value: string) {
  const normalized = value.trim();
  return normalized || null;
}

function toBusinessForm(profile?: BusinessProfile | null): typeof emptyBusinessForm {
  return {
    legalNameVi: profile?.legalNameVi ?? "",
    legalNameEn: profile?.legalNameEn ?? "",
    shortName: profile?.shortName ?? "",
    legalEntityType: profile?.legalEntityType ?? "",
    taxCode: profile?.taxCode ?? "",
    registeredAddress: profile?.registeredAddress ?? "",
    phone: profile?.phone ?? "",
    email: profile?.email ?? "",
    website: profile?.website ?? "",
    representativeName: profile?.representativeName ?? "",
    representativeTitle: profile?.representativeTitle ?? "",
    representativePhone: profile?.representativePhone ?? "",
    representativeEmail: profile?.representativeEmail ?? "",
  };
}

export function SettingsPage() {
  const auth = useAuth();
  const [store, setStore] = useState<StoreData | null>(
    auth.session
      ? {
          id: auth.session.store.id,
          name: auth.session.store.name,
          businessType: auth.session.store.businessType,
          timezone: "Asia/Ho_Chi_Minh",
        }
      : null,
  );
  const [storeForm, setStoreForm] = useState({
    name: auth.session?.store.name ?? "",
    businessType: auth.session?.store.businessType ?? "Tạp hóa",
    phone: "",
    email: "",
    website: "",
    address: "",
  });
  const [businessForm, setBusinessForm] = useState(emptyBusinessForm);
  const [profileMeta, setProfileMeta] = useState<BusinessProfileResponse["completeness"]>({
    completed: 0,
    total: 7,
    percent: 0,
    missing: [],
  });
  const [suggestions, setSuggestions] = useState<Partial<BusinessProfile>>({});
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  const canEdit = auth.session?.user.role === "OWNER" && !auth.demo && navigator.onLine;
  const profileStatus = useMemo(() => {
    if (profileMeta.percent === 100) return { tone: "success" as const, label: "Đã hoàn thiện" };
    if (profileMeta.percent > 0) return { tone: "info" as const, label: `${profileMeta.percent}% hoàn tất` };
    return { tone: "neutral" as const, label: "Chưa thiết lập" };
  }, [profileMeta.percent]);

  useEffect(() => {
    if (auth.demo || !navigator.onLine) return;
    void Promise.all([
      api<StoreData>("/store"),
      api<BusinessProfileResponse>("/business-profile"),
    ]).then(([currentStore, business]) => {
      setStore(currentStore);
      setStoreForm({
        name: currentStore.name,
        businessType: currentStore.businessType ?? "Tạp hóa",
        phone: currentStore.phone ?? "",
        email: currentStore.email ?? "",
        website: currentStore.website ?? "",
        address: currentStore.address ?? "",
      });
      setBusinessForm(toBusinessForm(business.profile));
      setProfileMeta(business.completeness);
      setSuggestions(business.suggestions ?? {});
    }).catch((error) => {
      setMessage(error instanceof Error ? error.message : "Không thể tải thiết lập.");
    });
  }, [auth.demo]);

  async function saveStore(event: React.FormEvent) {
    event.preventDefault();
    if (!canEdit) return;
    setBusy(true);
    setMessage("");
    try {
      const updated = await api<StoreData>("/store", {
        method: "PATCH",
        body: JSON.stringify({
          name: storeForm.name.trim(),
          businessType: optional(storeForm.businessType),
          phone: optional(storeForm.phone),
          email: optional(storeForm.email),
          website: optional(storeForm.website),
          address: optional(storeForm.address),
        }),
      });
      setStore(updated);
      setMessage("Đã cập nhật thông tin vận hành cửa hàng.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Không thể cập nhật cửa hàng.");
    } finally {
      setBusy(false);
    }
  }

  async function saveBusiness(event: React.FormEvent) {
    event.preventDefault();
    if (!canEdit) return;
    setBusy(true);
    setMessage("");
    try {
      const updated = await api<BusinessProfileResponse>("/business-profile", {
        method: "PUT",
        body: JSON.stringify({
          legalNameVi: optional(businessForm.legalNameVi),
          legalNameEn: optional(businessForm.legalNameEn),
          shortName: optional(businessForm.shortName),
          legalEntityType: businessForm.legalEntityType || null,
          taxCode: optional(businessForm.taxCode),
          registeredAddress: optional(businessForm.registeredAddress),
          phone: optional(businessForm.phone),
          email: optional(businessForm.email),
          website: optional(businessForm.website),
          representativeName: optional(businessForm.representativeName),
          representativeTitle: optional(businessForm.representativeTitle),
          representativePhone: optional(businessForm.representativePhone),
          representativeEmail: optional(businessForm.representativeEmail),
        }),
      });
      setBusinessForm(toBusinessForm(updated.profile));
      setProfileMeta(updated.completeness);
      setMessage("Đã lưu hồ sơ doanh nghiệp và ghi nhận audit trail.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Không thể lưu hồ sơ doanh nghiệp.");
    } finally {
      setBusy(false);
    }
  }

  function copyStoreContact() {
    setBusinessForm((current) => ({
      ...current,
      legalNameVi: current.legalNameVi || suggestions.legalNameVi || storeForm.name,
      taxCode: current.taxCode || suggestions.taxCode || "",
      registeredAddress:
        current.registeredAddress || suggestions.registeredAddress || storeForm.address,
      phone: current.phone || suggestions.phone || storeForm.phone,
      email: current.email || suggestions.email || storeForm.email,
      website: current.website || suggestions.website || storeForm.website,
      representativeName:
        current.representativeName || suggestions.representativeName || "",
    }));
    setMessage("Đã điền các trường còn trống từ thông tin cửa hàng. Kiểm tra lại trước khi lưu.");
  }

  return (
    <div className="page-stack">
      <PageHeader
        back
        title="Thiết lập cửa hàng"
        description="Thông tin vận hành và hồ sơ doanh nghiệp được tách rõ để dễ sử dụng, dễ mở rộng nhiều chi nhánh"
      />

      {auth.demo ? (
        <InlineNotice tone="info" title="Đang ở chế độ demo">
          Thiết lập trong demo không ghi lên server.
        </InlineNotice>
      ) : null}
      {!navigator.onLine && !auth.demo ? (
        <InlineNotice tone="warning" title="Đang ngoại tuyến">
          Có thể xem thiết lập đã lưu nhưng thay đổi cần kết nối mạng.
        </InlineNotice>
      ) : null}
      {message ? (
        <InlineNotice
          tone={message.startsWith("Đã lưu") || message.startsWith("Đã cập nhật") ? "success" : "info"}
          title={message}
        />
      ) : null}

      <form className="card product-form" onSubmit={saveStore}>
        <div className="form-section-title">
          <Store size={20} />
          <div>
            <h2>Thông tin cửa hàng</h2>
            <small>Dùng cho bán hàng, tồn kho, liên hệ và vận hành hằng ngày.</small>
          </div>
        </div>

        <div className="form-grid">
          <label>
            Tên cửa hàng
            <input
              value={storeForm.name}
              onChange={(event) => setStoreForm((current) => ({ ...current, name: event.target.value }))}
              disabled={!canEdit}
              required
            />
          </label>
          <label>
            Ngành hàng / lĩnh vực kinh doanh
            <select
              value={storeForm.businessType}
              onChange={(event) => setStoreForm((current) => ({ ...current, businessType: event.target.value }))}
              disabled={!canEdit}
            >
              <option>Tạp hóa</option>
              <option>Thực phẩm</option>
              <option>Mini mart</option>
              <option>Bán lẻ</option>
              <option>Ăn uống</option>
              <option>Dịch vụ</option>
              <option>Khác</option>
            </select>
          </label>
          <label>
            Số điện thoại
            <input
              inputMode="tel"
              value={storeForm.phone}
              onChange={(event) => setStoreForm((current) => ({ ...current, phone: event.target.value }))}
              disabled={!canEdit}
              placeholder="Ví dụ: 0901 234 567"
            />
          </label>
          <label>
            Email
            <input
              type="email"
              value={storeForm.email}
              onChange={(event) => setStoreForm((current) => ({ ...current, email: event.target.value }))}
              disabled={!canEdit}
              placeholder="cuahang@example.com"
            />
          </label>
          <label className="form-span">
            Website
            <input
              type="url"
              value={storeForm.website}
              onChange={(event) => setStoreForm((current) => ({ ...current, website: event.target.value }))}
              disabled={!canEdit}
              placeholder="https://example.com"
            />
          </label>
          <label className="form-span">
            Địa chỉ cửa hàng
            <input
              value={storeForm.address}
              onChange={(event) => setStoreForm((current) => ({ ...current, address: event.target.value }))}
              disabled={!canEdit}
              placeholder="Địa chỉ đang vận hành"
            />
          </label>
        </div>

        <div className="setting-row">
          <span>Múi giờ</span>
          <strong>{store?.timezone ?? "Asia/Ho_Chi_Minh"}</strong>
        </div>

        {canEdit ? (
          <button className="btn btn-primary btn-lg" disabled={busy || storeForm.name.trim().length < 2}>
            <Save size={20} />
            {busy ? "Đang lưu..." : "Lưu thông tin cửa hàng"}
          </button>
        ) : null}
      </form>

      <form className="card product-form" onSubmit={saveBusiness}>
        <div className="form-section-title">
          <Building2 size={20} />
          <div>
            <h2>Hồ sơ doanh nghiệp</h2>
            <small>Một hồ sơ pháp lý dùng chung cho toàn bộ cửa hàng / chi nhánh trong cùng tổ chức.</small>
          </div>
          <StatusBadge tone={profileStatus.tone}>{profileStatus.label}</StatusBadge>
        </div>

        <InlineNotice tone="info" title="Không bắt buộc để sử dụng BLedger">
          Điền càng đầy đủ thì hệ thống càng thuận tiện khi chuẩn bị hồ sơ thuế, chứng từ và báo cáo.
          BLedger chỉ yêu cầu trường cụ thể khi nghiệp vụ tương ứng thực sự cần.
        </InlineNotice>

        {canEdit ? (
          <button type="button" className="btn btn-secondary" onClick={copyStoreContact}>
            <Copy size={18} />
            Dùng thông tin cửa hàng cho các trường còn trống
          </button>
        ) : null}

        <div className="form-grid">
          <label className="form-span">
            Tên tiếng Việt
            <input
              value={businessForm.legalNameVi}
              onChange={(event) => setBusinessForm((current) => ({ ...current, legalNameVi: event.target.value }))}
              disabled={!canEdit}
              placeholder="Tên theo hồ sơ đăng ký"
            />
          </label>
          <label>
            Tên tiếng Anh
            <input
              value={businessForm.legalNameEn}
              onChange={(event) => setBusinessForm((current) => ({ ...current, legalNameEn: event.target.value }))}
              disabled={!canEdit}
            />
          </label>
          <label>
            Tên viết tắt
            <input
              value={businessForm.shortName}
              onChange={(event) => setBusinessForm((current) => ({ ...current, shortName: event.target.value }))}
              disabled={!canEdit}
            />
          </label>
          <label>
            Loại hình pháp lý
            <select
              value={businessForm.legalEntityType}
              onChange={(event) =>
                setBusinessForm((current) => ({
                  ...current,
                  legalEntityType: event.target.value as LegalEntityType | "",
                }))
              }
              disabled={!canEdit}
            >
              <option value="">Chưa thiết lập</option>
              {Object.entries(legalEntityLabels).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Mã số thuế
            <input
              inputMode="numeric"
              value={businessForm.taxCode}
              onChange={(event) => setBusinessForm((current) => ({ ...current, taxCode: event.target.value }))}
              disabled={!canEdit}
              placeholder="Ví dụ: 0312345678"
            />
          </label>
          <label className="form-span">
            Địa chỉ đăng ký
            <input
              value={businessForm.registeredAddress}
              onChange={(event) =>
                setBusinessForm((current) => ({ ...current, registeredAddress: event.target.value }))
              }
              disabled={!canEdit}
            />
          </label>
          <label>
            Số điện thoại
            <input
              inputMode="tel"
              value={businessForm.phone}
              onChange={(event) => setBusinessForm((current) => ({ ...current, phone: event.target.value }))}
              disabled={!canEdit}
            />
          </label>
          <label>
            Email
            <input
              type="email"
              value={businessForm.email}
              onChange={(event) => setBusinessForm((current) => ({ ...current, email: event.target.value }))}
              disabled={!canEdit}
            />
          </label>
          <label className="form-span">
            Website
            <input
              type="url"
              value={businessForm.website}
              onChange={(event) => setBusinessForm((current) => ({ ...current, website: event.target.value }))}
              disabled={!canEdit}
              placeholder="https://example.com"
            />
          </label>
        </div>

        <div className="form-section-title">
          <UserRound size={20} />
          <div>
            <h2>Người đại diện pháp luật</h2>
            <small>Thông tin liên hệ phục vụ chứng từ và hồ sơ khi cần.</small>
          </div>
        </div>

        <div className="form-grid">
          <label>
            Họ và tên
            <input
              value={businessForm.representativeName}
              onChange={(event) =>
                setBusinessForm((current) => ({ ...current, representativeName: event.target.value }))
              }
              disabled={!canEdit}
            />
          </label>
          <label>
            Chức danh
            <input
              value={businessForm.representativeTitle}
              onChange={(event) =>
                setBusinessForm((current) => ({ ...current, representativeTitle: event.target.value }))
              }
              disabled={!canEdit}
            />
          </label>
          <label>
            Số điện thoại
            <input
              inputMode="tel"
              value={businessForm.representativePhone}
              onChange={(event) =>
                setBusinessForm((current) => ({ ...current, representativePhone: event.target.value }))
              }
              disabled={!canEdit}
            />
          </label>
          <label>
            Email
            <input
              type="email"
              value={businessForm.representativeEmail}
              onChange={(event) =>
                setBusinessForm((current) => ({ ...current, representativeEmail: event.target.value }))
              }
              disabled={!canEdit}
            />
          </label>
        </div>

        {canEdit ? (
          <button className="btn btn-primary btn-lg" disabled={busy}>
            <Save size={20} />
            {busy ? "Đang lưu..." : "Lưu hồ sơ doanh nghiệp"}
          </button>
        ) : null}
      </form>

      <Card className="settings-summary">
        <div>
          <CheckCircle2 size={20} />
          <span>
            <strong>Hồ sơ dùng chung</strong>
            <small>Thông tin doanh nghiệp không bị lặp khi tổ chức có nhiều cửa hàng.</small>
          </span>
          <StatusBadge tone={profileStatus.tone}>{profileStatus.label}</StatusBadge>
        </div>
        <div>
          <Cloud size={20} />
          <span>
            <strong>Đồng bộ</strong>
            <small>PWA dùng IndexedDB và hàng đợi giao dịch cục bộ.</small>
          </span>
          <StatusBadge tone="success">Đã cấu hình</StatusBadge>
        </div>
        <div>
          <Settings size={20} />
          <span>
            <strong>Thiết bị bán hàng</strong>
            <small>Camera/barcode web-ready; printer Bluetooth là adapter tích hợp riêng.</small>
          </span>
          <StatusBadge tone="info">Mở rộng</StatusBadge>
        </div>
      </Card>
    </div>
  );
}
